// Villa 142 — static copy + helpers. Question content is served by the backend registry.

export const ENTRY = {
  heading: "Villa 142",
  note: "This is a private space to reflect on how you live, what matters to you, and the references that feel personal. You can complete it together or return to it separately whenever a thought arises.",
  privacy: "Your responses are private and visible only to our team.",
};

export const WELCOME = {
  heading: "A little space to reflect.",
  copy1: "There are no right answers here. Take your time, respond in the way that feels natural, and leave anything open that you would rather explore together in conversation.",
  copy2: "This is designed to be completed gradually — you can pause at any point and return whenever a thought arises.",
  button: "Begin",
};

export const CONFIRMATION = {
  heading: "Thank you.",
  copy: "Your reflections are now part of the conversation around Villa 142. We look forward to continuing the discussion together.",
  review: "Review our reflections",
  later: "Return later",
};

export const REACTION_OPTIONS = ["This connects", "Some elements connect", "Not for me", "I'm unsure"];

export const INPUT_TYPE_LABELS = {
  long_text: "Written",
  short_text: "Written",
  choice: "Choice",
  multi_choice: "Choices",
  scale: "Scale",
  people_list: "Household list",
  items_list: "List",
  visual_study: "Visual reaction",
};

export const FALLBACK_CONTRIBUTORS = [
  { id: "shruti", name: "Shruti", order: 0, is_shared: false, is_default: false, archived: false },
  { id: "aditya", name: "Aditya", order: 1, is_shared: false, is_default: false, archived: false },
  { id: "together", name: "Together", order: 2, is_shared: true, is_default: true, archived: false },
];

export function buildPeople(contributors, includeArchived = false) {
  const source = contributors && contributors.length ? contributors : FALLBACK_CONTRIBUTORS;
  const all = [...source].sort((a, b) => (a.order || 0) - (b.order || 0));
  const list = includeArchived ? all : all.filter((c) => !c.archived);
  const labels = {};
  const initials = {};
  all.forEach((c) => {
    labels[c.id] = c.name;
    initials[c.id] = (c.name || "?").charAt(0).toUpperCase();
  });
  const shared = all.find((c) => c.is_shared);
  const activeList = list.filter((c) => !c.archived);
  return {
    list: list.filter((c) => !c.archived),
    all,
    order: list.filter((c) => !c.archived).map((c) => c.id),
    orderAll: all.map((c) => c.id),
    labels,
    initials,
    sharedId: shared ? shared.id : null,
    defaultId: (activeList.find((c) => c.is_default) || activeList[0] || {}).id || null,
    name: (cid) => labels[cid] || cid,
    entryLabel: (cid) => (shared && cid === shared.id ? "Shared reflection" : `${labels[cid] || cid}'s reflection`),
  };
}

export function normalizeDefinition(data) {
  const chapters = (data.chapters || [])
    .filter((c) => c.active !== false)
    .map((ch) => ({ ...ch, questions: (ch.questions || []).filter((q) => q.active !== false) }));
  const vc = chapters.find((c) => c.id === "visual-cues");
  if (vc) {
    vc.questions = [
      ...(data.studies || [])
        .filter((s) => s.active !== false)
        .map((s, i) => ({
          id: `visual_${s.id}`,
          code: `V${String(i + 1).padStart(2, "0")}`,
          label: s.title || "Visual study",
          helper: null,
          input: "visual_study",
          config: { study: s },
          voice: true,
          uploads: null,
          active: true,
          conditional: null,
        })),
      ...vc.questions,
    ];
  }
  return { version: data.version, chapters: chapters.filter((c) => c.questions.length > 0) };
}

export function questionVisible(q, entriesMap) {
  if (!q.conditional) return true;
  const byC = entriesMap[q.conditional.question] || {};
  const answers = Object.values(byC).map((e) => e?.value?.choice).filter(Boolean);
  if (!answers.length) return q.conditional.default_show !== false;
  return answers.some((a) => (q.conditional.show || []).includes(a));
}

export function buildSteps(def, entriesMap) {
  const steps = [{ type: "welcome" }, { type: "contributor" }];
  (def?.chapters || []).forEach((ch, ci) => {
    const qs = ch.questions.filter((q) => questionVisible(q, entriesMap));
    if (!qs.length) return;
    steps.push({ type: "pause", chapter: ci });
    qs.forEach((q) => steps.push({ type: "question", chapter: ci, question: q }));
    steps.push({ type: "chapter_end", chapter: ci });
  });
  return steps;
}

export function chapterCounts(ch, entriesMap) {
  const visible = (ch?.questions || []).filter((q) => questionVisible(q, entriesMap));
  const answered = visible.filter((q) =>
    Object.values(entriesMap[q.id] || {}).some((e) => hasContent(e))
  ).length;
  return { answered, total: visible.length };
}

export function questionState(q, entriesMap, pendingSet) {
  const byC = entriesMap[q.id] || {};
  if (Object.values(byC).some((e) => hasContent(e))) return "reflected";
  if (pendingSet && pendingSet.has(q.id)) return "draft";
  return "empty";
}

export function valueHasContent(v) {
  if (!v) return false;
  if (v.text && String(v.text).trim()) return true;
  if (v.choice) return true;
  if (v.choices && v.choices.length) return true;
  if (typeof v.value === "number") return true;
  if (v.tags && v.tags.length) return true;
  if (v.chips && v.chips.length) return true;
  if (v.note && String(v.note).trim()) return true;
  if (v.other && String(v.other).trim()) return true;
  if (v.reaction) return true;
  if (v.guests && String(v.guests).trim()) return true;
  if (v.items && v.items.some((it) => Object.entries(it).some(([k, val]) => k !== "id" && String(val || "").trim()))) return true;
  if (v.details && valueHasContent(v.details)) return true;
  if (v.fields && Object.values(v.fields).some((x) => String(x || "").trim())) return true;
  return false;
}

export function hasContent(entry) {
  if (!entry) return false;
  if ((entry.media || []).length) return true;
  return valueHasContent(entry.value);
}

export function summarizeEntry(question, entry) {
  const v = entry?.value || {};
  const cfg = question?.config || {};
  const lines = [];
  if (v.reaction) lines.push(v.reaction);
  if (v.choice) lines.push(v.choice);
  const ef = cfg.extraField;
  if (ef && v[ef.key] && String(v[ef.key]).trim()) lines.push(`${ef.label} ${v[ef.key]}`);
  if (typeof v.value === "number") lines.push(`${v.value} of 5${cfg.left ? ` · ${cfg.left} → ${cfg.right}` : ""}`);
  if (v.choices && v.choices.length) lines.push(v.choices.join(" · "));
  if (v.chips && v.chips.length) lines.push(v.chips.join(" · "));
  if (v.other && String(v.other).trim()) lines.push(`Other: ${v.other}`);
  if (v.text && String(v.text).trim()) lines.push(v.text);
  const details = v.details || {};
  if (details.text && String(details.text).trim()) lines.push(details.text);
  if (details.items && details.items.length) {
    const dfields = cfg.details?.fields;
    details.items.forEach((it) => {
      if (dfields && dfields.length) {
        const parts = dfields.map((f) => (it[f.key] && String(it[f.key]).trim() ? `${f.label} — ${it[f.key]}` : null)).filter(Boolean);
        if (parts.length) lines.push(`• ${parts.join(" · ")}`);
      } else if ((it.label || "").trim()) {
        lines.push(`— ${it.label}`);
      }
    });
  }
  if (details.fields) {
    (cfg.details?.fields || []).forEach((f) => {
      const val = details.fields[f.key];
      if (val && String(val).trim()) lines.push(`${f.label}: ${val}`);
    });
  }
  if (v.items && v.items.length) {
    v.items.forEach((it) => {
      const parts = (cfg.fields || []).map((f) => it[f.key]).filter((x) => x && String(x).trim());
      if (parts.length) lines.push(`— ${parts.join(" · ")}`);
      else if ((it.label || "").trim()) lines.push(`— ${it.label}`);
    });
  }
  if (v.tags && v.tags.length) lines.push(v.tags.join(" · "));
  if (v.note && String(v.note).trim()) lines.push(v.note);
  return lines;
}

export function findQuestion(def, qid) {
  for (const ch of def?.chapters || []) {
    const q = ch.questions.find((x) => x.id === qid);
    if (q) return q;
  }
  return null;
}

export const fmtDate = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" });
};

export const fmtVoiceDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", hour12: true });
  const sameDay =
    d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  if (sameDay) return `Today, ${time}`;
  const day = d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
  return `${day}, ${time}`;
};

export const fmtSeconds = (s) => {
  if (s == null || Number.isNaN(Number(s))) return "";
  const n = Math.round(Number(s));
  return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, "0")}`;
};

export const VOICE_GUIDANCE = "Up to three voice notes, three minutes each. Add them anytime.";
export const voiceGuidance = () => VOICE_GUIDANCE;
